// cloudgrid status [<name>] — backlog 121 (no args = org dashboard) + M8a.1
// (with arg = entity drill-down).
//
// Resolution:
//   - <name> arg given           → entity drill-down (or trace snapshot when
//                                  the arg matches the trace-id shape). The
//                                  server resolves UUID or display_name.
//   - <name> arg omitted          → org dashboard summary. The dashboard
//                                  aggregates entities + activity for the
//                                  active org. Per backlog 121, the eventual
//                                  resting place for this aggregation is a
//                                  server-side `GET /api/v2/grids/:id/dashboard`
//                                  endpoint; v0.8.11 ships a CLI-only fan-out
//                                  over the existing runtimes + inspirations
//                                  list routes so the verb is useful today.
//
// Linked-directory users who want "what's MY entity doing?" use
// `cloudgrid info` (no args takes the linked entity).
//
// Sample human output for the drill-down case:
//
//   my-dashboard · live
//     Outlet:      https://my-dashboard.atomic.cloudgrid.io
//     Replicas:    1/1 ready
//     Last deploy: 3 hours ago  by gilad@atomiclabs.io  (2m 14s)
//     Health:      healthy
//
//   my-dashboard · charged
//     Not yet live. Run: cloudgrid plug
//
// Sample dashboard output:
//
//   atomic · 12 entities
//     Runtimes:    7 live · 1 unhealthy · 2 charged · 1 stopped
//     Inspirations:    18
//     Outlet root: https://atomic.cloudgrid.io
//
//   Recent:
//     2h ago  runtime  my-dashboard         live          https://my-dashboard.atomic.cloudgrid.io
//     6h ago  inspir   q2-deck              live          https://atomic.cloudgrid.io/q2-deck
//     ...
//
//   Unhealthy: 1
//     billing-prod   https://billing-prod.atomic.cloudgrid.io

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';
import { handleV2Error } from '../handle-error.js';
import { isTraceId } from '../services/trace-format.js';
import type { DeployTrace } from '../services/plug-poll.js';
import { renderSteps } from '../render/multi-step.js';
import { formatElapsedHuman } from '../services/deploys-log.js';
import type {
  V2RuntimeSummary,
  V2InspirationSummary,
} from '../../../shared/src/v1-types.js';

// ── API response type ─────────────────────────────────────────────────────

export interface V2RuntimeDetailResponse {
  entity_id: string;
  name: string;
  display_name: string;
  kind: 'app' | 'agent';
  realm: string;
  url: string;
  fallback_urls: string[];
  status: 'charged' | 'live' | 'live_unhealthy' | 'stopped' | 'archived' | 'expired';
  // The entity's visibility mode (private | space | authenticated | org | link
  // | public). Present on the runtime detail response; optional here so older
  // server builds that predate the field don't break the type.
  visibility?: string;
  replica_count: { ready: number; desired: number } | null;
  last_deploy: {
    deployed_at: string;
    deployed_by: string;
    status: 'building' | 'live' | 'failed' | 'rolled_back';
    duration_seconds: number | null;
  } | null;
  health: unknown[];
  overallHealth: 'healthy' | 'degraded' | 'failing';
  // owner is null when the owner's user record was purged (account deletion);
  // getRuntimeDetails tolerates this and returns `owner: null` by design.
  owner: { user_id: string; name: string; slug: string; email: string } | null;
  org: { org_id: string; slug: string; name: string };
  space: unknown | null;
  agent_block: unknown | null;
  created_at: string;
  updated_at: string;
}

// ── Time formatting helpers ───────────────────────────────────────────────

export function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return 'unknown';
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function formatDuration(secs: number | null): string {
  if (secs === null) return '';
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// ── Human-readable rendering (entity drill-down) ──────────────────────────

function renderHuman(entity: V2RuntimeDetailResponse): string {
  const label = entity.display_name || entity.name;
  const lines: string[] = [];

  lines.push(`${label} · ${entity.status}`);

  if (entity.status === 'charged') {
    lines.push(`  Not yet live. Run: cloudgrid plug`);
    return lines.join('\n');
  }

  lines.push(`  Outlet:      ${entity.url}`);

  if (entity.replica_count !== null) {
    const { ready, desired } = entity.replica_count;
    lines.push(`  Replicas:    ${ready}/${desired} ready`);
  } else {
    lines.push(`  Replicas:    —`);
  }

  if (entity.last_deploy !== null) {
    const ago = formatRelativeTime(entity.last_deploy.deployed_at);
    const by = entity.last_deploy.deployed_by;
    const dur = entity.last_deploy.duration_seconds !== null
      ? `  (${formatDuration(entity.last_deploy.duration_seconds)})`
      : '';
    lines.push(`  Last deploy: ${ago}  by ${by}${dur}`);
  } else {
    lines.push(`  Last deploy: —`);
  }

  lines.push(`  Health:      ${entity.overallHealth}`);

  return lines.join('\n');
}

// ── Dashboard types (backlog 121) ─────────────────────────────────────────
//
// The eventual server-side aggregator `GET /api/v2/grids/:id/dashboard`
// returns this shape. v0.8.11 builds it client-side over the existing
// list routes; the response matches what the server will produce so the
// switchover is a one-line client change.

interface RuntimeTotals {
  live: number;
  live_unhealthy: number;
  charged: number;
  stopped: number;
  archived: number;
  total: number;
}

interface InspirationTotals {
  total: number;
}

interface RecentItem {
  name: string;
  display_name: string;
  realm: 'runtime' | 'inspiration';
  status: string;
  url: string;
  updated_at: string;
}

interface RegressionItem {
  name: string;
  url: string;
  reason: string | null;
}

export interface OrgDashboard {
  org_slug: string;
  totals: {
    runtimes: RuntimeTotals;
    inspirations: InspirationTotals;
  };
  recent: RecentItem[];
  regressions: RegressionItem[];
  /**
   * Token-class usage rollup. Empty in v0.8.11 — the AI gateway side has not
   * yet shipped a per-org usage endpoint. The structure is the agreed shape;
   * the server-side aggregator fills it once the rollup endpoint exists.
   */
  ai_usage: {
    by_class: Record<string, { used: number; allowed: number | null }>;
    note?: string;
  };
}

// ── Dashboard fetcher (client-side aggregation) ───────────────────────────

async function fetchDashboard(client: IV2Client): Promise<OrgDashboard> {
  const orgSlug = client.getOrgSlug() ?? '';
  const [runtimesRes, inspirationsRes] = await Promise.all([
    client.get<{ items: V2RuntimeSummary[] }>('/api/v2/runtimes', { limit: 200, kind: 'all' }),
    client.get<{ items: V2InspirationSummary[] }>('/api/v2/inspirations', { limit: 200 }),
  ]);

  const runtimes = runtimesRes.items ?? [];
  const inspirations = inspirationsRes.items ?? [];

  const totals: RuntimeTotals = {
    live: 0,
    live_unhealthy: 0,
    charged: 0,
    stopped: 0,
    archived: 0,
    total: runtimes.length,
  };
  const regressions: RegressionItem[] = [];
  for (const r of runtimes) {
    switch (r.status) {
      case 'live':
        totals.live++;
        break;
      case 'live_unhealthy':
        totals.live_unhealthy++;
        regressions.push({ name: r.name, url: r.url, reason: null });
        break;
      case 'charged':
        totals.charged++;
        break;
      case 'stopped':
        totals.stopped++;
        break;
      case 'archived':
      case 'expired':
        totals.archived++;
        break;
    }
  }

  // Recent — top 5 most-recently-updated across both realms.
  const all: RecentItem[] = [
    ...runtimes.map((r) => ({
      name: r.name,
      display_name: r.display_name,
      realm: 'runtime' as const,
      status: r.status,
      url: r.url,
      updated_at: r.updated_at,
    })),
    ...inspirations.map((i) => ({
      name: i.name,
      display_name: i.display_name,
      realm: 'inspiration' as const,
      // Inspirations don't carry a status enum in V2InspirationSummary; use
      // 'live' as the default (every inspiration in the list is live).
      status: 'live',
      url: i.url,
      updated_at: i.created_at,
    })),
  ];
  all.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
  const recent = all.slice(0, 5);

  return {
    org_slug: orgSlug,
    totals: {
      runtimes: totals,
      inspirations: { total: inspirations.length },
    },
    recent,
    regressions,
    ai_usage: {
      by_class: {},
      note: 'AI usage rollup pending — server-side aggregator (GET /api/v2/grids/:id/dashboard) not yet wired.',
    },
  };
}

function renderDashboard(d: OrgDashboard): string {
  const lines: string[] = [];
  const totalEntities = d.totals.runtimes.total + d.totals.inspirations.total;
  const header = d.org_slug
    ? `${d.org_slug} · ${totalEntities} ${totalEntities === 1 ? 'entity' : 'entities'}`
    : `${totalEntities} ${totalEntities === 1 ? 'entity' : 'entities'}`;
  lines.push(header);

  const r = d.totals.runtimes;
  const runtimeParts: string[] = [];
  if (r.live > 0) runtimeParts.push(`${r.live} live`);
  if (r.live_unhealthy > 0) runtimeParts.push(`${r.live_unhealthy} unhealthy`);
  if (r.charged > 0) runtimeParts.push(`${r.charged} charged`);
  if (r.stopped > 0) runtimeParts.push(`${r.stopped} stopped`);
  if (r.archived > 0) runtimeParts.push(`${r.archived} archived`);
  const runtimeLine = runtimeParts.length > 0 ? runtimeParts.join(' · ') : 'none';
  lines.push(`  Runtimes:     ${runtimeLine}`);
  lines.push(`  Inspirations: ${d.totals.inspirations.total}`);

  if (d.recent.length > 0) {
    lines.push('');
    lines.push('Recent:');
    for (const item of d.recent) {
      const ago = formatRelativeTime(item.updated_at).padEnd(7);
      const realm = (item.realm === 'runtime' ? 'runtime' : 'inspir ').padEnd(8);
      const name = (item.display_name || item.name).padEnd(28);
      const status = item.status.padEnd(14);
      lines.push(`  ${ago} ${realm} ${name} ${status} ${item.url}`);
    }
  } else {
    lines.push('');
    lines.push('Recent: none');
  }

  if (d.regressions.length > 0) {
    lines.push('');
    lines.push(`Unhealthy: ${d.regressions.length}`);
    for (const r of d.regressions) {
      lines.push(`  ${r.name}   ${r.url}`);
    }
  }

  if (d.ai_usage.note) {
    lines.push('');
    lines.push(`AI usage: ${d.ai_usage.note}`);
  }

  return lines.join('\n');
}

// ── Command implementation ────────────────────────────────────────────────

export interface StatusOpts {
  json?: boolean;
  output?: string;
}

/**
 * Core implementation. Accepts an optional `client` override for unit tests.
 * In production, the client is created via `getV2Client()`.
 *
 * Routing:
 *   - no arg                                         → org dashboard.
 *   - arg matches a trace-id shape (`d_<...>_<6>`)   → trace snapshot via
 *                                                      `GET /deploys/:trace_id`.
 *   - else                                           → entity drill-down.
 */
export async function runStatus(
  nameArg: string | undefined,
  opts: StatusOpts,
  client?: IV2Client,
): Promise<void> {
  const apiClient = client ?? (await getV2Client());
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });

  if (nameArg === undefined) {
    const dashboard = await fetchDashboard(apiClient);
    out.json(() => dashboard);
    out.human(() => renderDashboard(dashboard));
    return;
  }

  if (isTraceId(nameArg)) {
    const trace = await apiClient.get<DeployTrace>(`/deploys/${nameArg}`);
    out.json(() => trace);
    out.human(() => renderTraceSnapshot(trace));
    return;
  }

  const entity = await apiClient.get<V2RuntimeDetailResponse>(
    `/api/v2/runtimes/${encodeURIComponent(nameArg)}`,
  );
  out.json(() => entity);
  out.human(() => renderHuman(entity));
}

// ── Trace snapshot rendering (072-B) ──────────────────────────────────────

export function renderTraceSnapshot(trace: DeployTrace): string {
  const elapsed = computeElapsedMs(trace);
  const elapsedLabel =
    trace.status === 'success' || trace.status === 'failed'
      ? formatElapsedHuman(elapsed)
      : `${formatElapsedHuman(elapsed)} elapsed`;
  const lines: string[] = [];
  lines.push(`trace ${trace.trace_id} — ${trace.status} (${elapsedLabel})`);
  if (trace.steps.length > 0) {
    lines.push(renderSteps(trace.steps));
  }
  if (trace.status === 'success' && trace.result?.url) {
    lines.push(`  Outlet: ${trace.result.url}`);
  }
  if (trace.status === 'failed' && trace.error) {
    lines.push(`  ${trace.error.code}: ${trace.error.message}`);
    // Backlog 219 — when the plug-fix agent persisted a suggestion on the
    // event row, surface its message on a follow-up `AI suggests: ...` line
    // so retrospective `cloudgrid status <trace>` lookups see the same
    // guidance the `cloudgrid plug` failure surface showed at the time.
    const ai = (trace.error as { ai_suggestion?: { message?: string } }).ai_suggestion;
    if (ai && typeof ai.message === 'string' && ai.message.trim().length > 0) {
      lines.push(`  AI suggests: ${ai.message.trim()}`);
    }
  }
  return lines.join('\n');
}

function computeElapsedMs(trace: DeployTrace): number {
  if (trace.started_at === null) return 0;
  const start = Date.parse(trace.started_at);
  if (!Number.isFinite(start)) return 0;
  const end =
    trace.completed_at !== null ? Date.parse(trace.completed_at) : Date.now();
  if (!Number.isFinite(end)) return 0;
  return Math.max(0, end - start);
}

// ── Commander command export ──────────────────────────────────────────────

export const statusCommand = new Command('status')
  .description(
    'Show grid dashboard (no args), entity detail (with name), or a deploy snapshot (with trace id)',
  )
  .argument(
    '[name-or-trace]',
    'Entity name/UUID, or a deploy trace id (no arg = grid dashboard)',
  )
  .option('--json', 'Machine-readable JSON output (alias of -o json)');

addOutputOption(statusCommand).action(async (name: string | undefined, opts: StatusOpts) => {
  try {
    await runStatus(name, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
